import React, { useState, useMemo } from "react";
import Select from "react-select";
import { Tooltip } from "react-tippy";
import { Checkbox } from "@mui/material";
import { ReactComponent as ChevronDown } from "../../../../assets/icons/chevron-down.svg";
import {
  addendumFilterOption,
  buildEpisodeOptions,
  buildChapterOptions,
  getSelectionLabel,
} from "./addendum.utils";
import "./AddendumSelect.scss";

const AddendumOption = ({
  innerRef,
  innerProps,
  isSelected,
  isFocused,
  label,
  data,
  selectProps,
}) => {
  const isHighlighted =
    selectProps.hoveredValue !== null
      ? selectProps.hoveredValue === data.value
      : isFocused;

  return (
    <div
      ref={innerRef}
      {...innerProps}
      onMouseEnter={() => selectProps.onOptionHover(data.value)}
      onMouseLeave={() => selectProps.onOptionHover(null)}
      className={`addendum-select__option${isHighlighted ? " addendum-select__option--focused" : ""}`}
    >
      <Checkbox
        checked={isSelected}
        size="small"
        disableRipple
        sx={{ color: "#36355A", "&.Mui-checked": { color: "#36355A" } }}
      />
      <span>{label}</span>
    </div>
  );
};

const AddendumDropdownIndicator = () => (
  <div style={{ padding: "8px 16px 0 16px" }}>
    <ChevronDown />
  </div>
);

const AddendumGroupHeading = ({ data }) => {
  if (data.label === "selected") return null;
  return <div className="addendum-select__group-separator" />;
};

const AddendumValueContainer = ({ children, getValue, selectProps }) => {
  const count = getValue().length;
  const { menuIsOpen, placeholder, inputValue } = selectProps;
  const [, input] = children;

  const selected = getValue();
  const numbers = selected
    .map((opt) => opt.label.replace(/\D+/g, "").padStart(2, "0"))
    .sort((a, b) => Number(a) - Number(b));
  const prefix = selected[0]?.label.startsWith("Ep")
    ? "Episódios"
    : "Capítulos";
  const formattedNumbers =
    numbers.length <= 1
      ? numbers.join("")
      : `${numbers.slice(0, -1).join(", ")} e ${numbers[numbers.length - 1]}`;
  const tooltipContent = (
    <span className="addendum-select__tooltip-list">
      {`${prefix}: ${formattedNumbers}`}
    </span>
  );

  return (
    <div className="addendum-select__value-container">
      {input}
      {!menuIsOpen && count > 0 ? (
        <Tooltip
          position="top"
          trigger="mouseenter"
          animateFill={false}
          duration={150}
          arrow={true}
          html={tooltipContent}
          theme="dark"
        >
          <span className="addendum-select__counter">
            {getSelectionLabel(count)}
          </span>
        </Tooltip>
      ) : !menuIsOpen || !inputValue ? (
        <span className="addendum-select__placeholder">{placeholder}</span>
      ) : null}
    </div>
  );
};

const AddendumSelect = ({
  dramaturgyList = [],
  episodeList = [],
  isMaterial = false,
  onEpisodeIdsChange,
  initialIds = [],
  darkBackgroundColor = true,
}) => {
  const buildInitialValue = () => {
    if (!initialIds?.length) return [];
    const opts = isMaterial
      ? buildEpisodeOptions(episodeList)
      : buildChapterOptions(dramaturgyList);
    return opts.filter((o) => initialIds.includes(o.value));
  };

  const [value, setValue] = useState(buildInitialValue);
  const [inputValue, setInputValue] = useState("");
  const [hoveredValue, setHoveredValue] = useState(null);

  const handleChange = (selected) => {
    const newValue = selected || [];
    setValue(newValue);
    if (onEpisodeIdsChange)
      onEpisodeIdsChange(newValue.map((item) => item.value));
  };

  const options = useMemo(
    () =>
      isMaterial
        ? buildEpisodeOptions(episodeList)
        : buildChapterOptions(dramaturgyList),
    [isMaterial, dramaturgyList, episodeList]
  );

  const groupedOptions = useMemo(() => {
    if (darkBackgroundColor) return options;
    const selectedValues = new Set(value.map((v) => v.value));
    const selected = options.filter((o) => selectedValues.has(o.value));
    const unselected = options.filter((o) => !selectedValues.has(o.value));
    if (!selected.length) return options;
    if (!unselected.length) return options;
    return [
      { label: "selected", options: selected },
      { label: "unselected", options: unselected },
    ];
  }, [options, value, darkBackgroundColor]);

  return (
    <fieldset className="fieldset">
      <label>
        {isMaterial ? "Associar Episódio(s)" : "Associar Capítulo(s)"}
      </label>

      <Select
        isMulti
        isSearchable
        options={groupedOptions}
        value={value}
        onChange={handleChange}
        inputValue={inputValue}
        onInputChange={(val, { action }) => {
          if (action === "input-change") setInputValue(val);
        }}
        onMenuClose={() => setInputValue("")}
        filterOption={addendumFilterOption}
        placeholder="Busque um capítulo"
        hideSelectedOptions={false}
        closeMenuOnSelect={false}
        components={{
          Option: AddendumOption,
          ValueContainer: AddendumValueContainer,
          DropdownIndicator: AddendumDropdownIndicator,
          GroupHeading: AddendumGroupHeading,
        }}
        isClearable={false}
        backspaceRemovesValue={false}
        noOptionsMessage={() => "Nenhum resultado encontrado"}
        classNamePrefix="addendum-select"
        className={!darkBackgroundColor ? "addendum-select--light" : undefined}
        hoveredValue={hoveredValue}
        onOptionHover={setHoveredValue}
      />
    </fieldset>
  );
};

export default AddendumSelect;
